import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  AppstoreOutlined,
  UnorderedListOutlined,
  ReloadOutlined,
  PlusOutlined,
  EditOutlined,
  EyeOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import {
  Button, Card, Col, Empty, Input, Modal, Popconfirm, Row, Segmented, Space, Spin, Tag, Tooltip, Typography, message,
} from "antd";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Constants from "../Utils/Constants";

export default function ProgrammeMenu({ defaultPatientId = null }) {
  const { t, i18n } = useTranslation("programme", { useSuspense: false });
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const authHeaders = { Authorization: "Bearer " + token, "Accept-Language": i18n.language };
  const jsonHeaders = { ...authHeaders, "Content-Type": "application/json" };

  // UI
  const [view, setView] = useState("grid");
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: "",
    patientId: defaultPatientId,
    startDate: "",
    endDate: "",
    durationWeeks: 12,
    timeOfDay: "MORNING",
    frequencyDays: ["Lun", "Mer", "Ven"],
  });

  // data
  const [programmes, setProgrammes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);

  const openModal = (text, isError = false) => {
    Modal[isError ? "error" : "success"]({
      title: isError ? (t("label_error") || "Erreur") : (t("label_success") || "Succ�s"),
      content: text,
    });
  };

  const fetchProgrammes = () => {
    setFetching(true);
    const q = form.patientId ? `?patientId=${encodeURIComponent(form.patientId)}` : "";
    axios
      .get(`${Constants.SERVER_URL}/Programme/programmes${q}`, { headers: authHeaders })
      .then((res) => setProgrammes(res.data || []))
      .catch((err) =>
        openModal(err?.response?.data?.error || err?.message || "Erreur de chargement", true)
      )
      .finally(() => setFetching(false));
  };

  useEffect(() => {
    setLoading(true);
    fetchProgrammes();
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmitCreate = () => {
    const payload = {
      name: String(form.name || "").trim(),
      PatientId: form.patientId || null,
      startDate: form.startDate || null,
      endDate: form.endDate || null,
      durationWeeks: Number(form.durationWeeks) || null,
      timeOfDay: form.timeOfDay || null,
      frequencyDays: form.frequencyDays || [],
      status: "ACTIVE",
      cycles: [], // on cr�e vide; on ajoutera depuis l'�cran d�tail
    };

    if (!payload.name) {
      message.warning(t("label_programme_name_required") || "Nom du programme requis");
      return;
    }

    setCreating(true);
    axios
      .post(`${Constants.SERVER_URL}/Programme/programmes`, payload, { headers: jsonHeaders })
      .then((res) => {
        message.success(t("label_programme_created") || "Programme cr��");
        setCreateOpen(false);
        setForm((s) => ({ ...s, name: "" }));
        const id = res?.data?.id;
        if (id) navigate(`/programmes/${id}`, { replace: true });
        else fetchProgrammes();
      })
      .catch((err) =>
        openModal(err?.response?.data?.error || err?.message || "Cr�ation impossible", true)
      )
      .finally(() => setCreating(false));
  };

  const deleteProgramme = (id) => {
    // (optionnel) si tu exposes DELETE /Programme/programmes/:id
    // ici on ne l'a pas cr��; tu peux juste afficher un message
    openModal(t("label_delete_not_implemented") || "Suppression non impl�ment�e", true);
  };

  // recherche simple
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return programmes;
    return programmes.filter((p) => {
      const name = String(p?.name || "").toLowerCase();
      const desc = String(p?.description || "").toLowerCase();
      return name.includes(q) || desc.includes(q);
    });
  }, [programmes, search]);

  return (
    <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
      <Space align="center" style={{ justifyContent: "space-between", width: "100%" }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          {t("label_programmes_title") || "Programmes"}
        </Typography.Title>
        <Space wrap>
          <Segmented
            value={view}
            onChange={setView}
            options={[
              { value: "grid", icon: <AppstoreOutlined />, label: t("label_grid") || "Grille" },
              { value: "list", icon: <UnorderedListOutlined />, label: t("label_list") || "Liste" },
            ]}
          />
          <Tooltip title={t("label_refresh") || "Rafra�chir"}>
            <Button icon={<ReloadOutlined spin={fetching} />} onClick={fetchProgrammes} />
          </Tooltip>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            {t("label_new_programme") || "Nouveau programme"}
          </Button>
        </Space>
      </Space>

      <Space>
        <Input.Search
          allowClear
          placeholder={t("label_search_programme") || "Rechercher�"}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 420 }}
        />
        {fetching && <Tag>MAJ�</Tag>}
      </Space>

      <div style={{ minHeight: 240 }}>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
            <Spin tip={t("label_loading_programmes") || "Chargement des programmes�"} />
          </div>
        ) : filtered.length === 0 ? (
          <Empty description={t("label_no_programmes") || "Aucun programme."} />
        ) : view === "grid" ? (
          <Row gutter={[16, 16]}>
            {filtered.map((p) => (
              <Col key={p.id} xs={24} sm={12} md={8} lg={6} xl={6}>
                <Card
                  hoverable
                  title={
                    <Typography.Text strong ellipsis style={{ maxWidth: 200 }}>
                      {p.name || "Sans nom"}
                    </Typography.Text>
                  }
                  actions={[
                    <Tooltip title={t("label_edit") || "�diter"} key="edit">
                      <EditOutlined onClick={() => navigate(`/programmes/${p.id}`)} />
                    </Tooltip>,
                    <Tooltip title={t("label_view") || "Voir"} key="view">
                      <EyeOutlined onClick={() => navigate(`/programmes/${p.id}`)} />
                    </Tooltip>,
                    <Popconfirm
                      title={t("label_confirm_delete") || "Supprimer ce programme ?"}
                      okText={t("label_delete") || "Supprimer"}
                      cancelText={t("label_cancel") || "Annuler"}
                      okButtonProps={{ danger: true }}
                      onConfirm={() => deleteProgramme(p.id)}
                      key="delete"
                    >
                      <DeleteOutlined style={{ color: "#ff4d4f" }} />
                    </Popconfirm>,
                  ]}
                >
                  <Space direction="vertical" size={4} style={{ width: "100%" }}>
                    <Typography.Paragraph ellipsis={{ rows: 2 }}>
                      {p.description || "�"}
                    </Typography.Paragraph>
                    <Space wrap>
                      {Array.isArray(p.frequencyDays) &&
                        p.frequencyDays.map((d) => <Tag key={d}>{d}</Tag>)}
                    </Space>
                  </Space>
                </Card>
              </Col>
            ))}
          </Row>
        ) : (
          <Space direction="vertical" style={{ width: "100%" }}>
            {filtered.map((p) => (
              <Card key={p.id} size="small">
                <Space align="start" style={{ width: "100%", justifyContent: "space-between" }}>
                  <Space direction="vertical" size={0}>
                    <Typography.Text strong>{p.name || "Sans nom"}</Typography.Text>
                    <Typography.Text type="secondary">{p.description || "�"}</Typography.Text>
                  </Space>
                  <Space>
                    <Button icon={<EditOutlined />} onClick={() => navigate(`/programmes/${p.id}`)}>
                      {t("label_edit") || "�diter"}
                    </Button>
                    <Button icon={<EyeOutlined />} onClick={() => navigate(`/programmes/${p.id}`)}>
                      {t("label_view") || "Voir"}
                    </Button>
                    <Popconfirm
                      title={t("label_confirm_delete") || "Supprimer ce programme ?"}
                      okText={t("label_delete") || "Supprimer"}
                      cancelText={t("label_cancel") || "Annuler"}
                      okButtonProps={{ danger: true }}
                      onConfirm={() => deleteProgramme(p.id)}
                    >
                      <Button danger icon={<DeleteOutlined />}>
                        {t("label_delete") || "Supprimer"}
                      </Button>
                    </Popconfirm>
                  </Space>
                </Space>
              </Card>
            ))}
          </Space>
        )}
      </div>

      <Modal
        title={t("label_create_programme_title") || "Cr�er un programme"}
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        okText={t("label_create") || "Cr�er"}
        okButtonProps={{ loading: creating }}
        onOk={onSubmitCreate}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Input
            placeholder={t("label_programme_name") || "Nom du programme"}
            value={form.name}
            onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
            maxLength={80}
          />
          <Input
            placeholder={t("label_patient_id") || "PatientId (optionnel)"}
            value={form.patientId ?? ""}
            onChange={(e) => setForm((s) => ({ ...s, patientId: e.target.value }))}
          />
          <Input
            placeholder={t("label_start_date") || "Date d�but (AAAA-MM-JJ)"}
            value={form.startDate}
            onChange={(e) => setForm((s) => ({ ...s, startDate: e.target.value }))}
          />
          <Input
            placeholder={t("label_end_date") || "Date fin (AAAA-MM-JJ)"}
            value={form.endDate}
            onChange={(e) => setForm((s) => ({ ...s, endDate: e.target.value }))}
          />
        </Space>
      </Modal>
    </div>
  );
}
